import { v4 as uuidv4 } from 'uuid';

import { isHeaderItem } from '../list.helpers';
import {
  EnforcedGroupListItems,
  EnforcedListItems,
  GroupListItem,
  ListItem,
  ListItemVariant,
  ListValue,
  StatefulList,
  ValueListItem,
} from '../stateful-list.types';

const PREFIX = 'focusable-item';

/**
 * Builder for creating a single group of list items.
 *
 * - Allows optional headers
 * - Ensures at least one item is present
 * - When the group is disabled, all value items inside are automatically disabled
 */
export class GroupBuilder {
  private items: ListItem[] = [];
  private valueMap = new Map<ListValue, ValueListItem>();
  constructor(private readonly id: string) {}

  /**
   * Adds a header to the group.
   *
   * @param label - The header label (can be string, JSX, etc.)
   * @returns this (for chaining)
   */
  addHeader(label: string): this {
    this.items.push({ isHeader: true, label });
    return this;
  }

  /**
   * Adds a value item to the group.
   *
   * @param value - Unique identifier for the item
   * @param label - Label to display for the item
   * @returns this (for chaining)
   */
  addItem(
    value: ListValue,
    label: string,
    itemCallback?: ValueListItem['itemCallback'],
    icon?: React.ComponentType,
    variant?: ListItemVariant,
  ): this {
    const normalizedValue = value
      .toString()
      .replace(/[^a-zA-Z0-9]/g, '-')
      .toLowerCase();
    const item = {
      value,
      label,
      icon,
      focusId: `${PREFIX}-${normalizedValue}-${this.id}`,
      itemCallback,
      variant,
    };
    this.items.push(item);
    this.valueMap.set(value, item);
    return this;
  }

  /**
   * Builds and returns a complete GroupListItem.
   * Will throw an error if no items were added.
   */
  build(): GroupListItem {
    if (this.items.length === 0) {
      throw new Error('Group must contain at least one item.');
    }

    return { items: this.items as EnforcedListItems, valueItemMap: this.valueMap };
  }

  static create(id: string): GroupBuilder {
    return new GroupBuilder(id);
  }
}

/**
 * Builder for creating a full MultiSelectList.
 *
 * - Ensures at least one group is present
 * - Accepts one or more configured GroupBuilders
 */
export class ListBuilder {
  private groups: GroupListItem[] = [];
  private id: string; // Unique identifier for the list
  private valueItemMap = new Map<ListValue, ValueListItem>();

  constructor(id?: string) {
    this.id = id ?? uuidv4();
  }

  /**
   * Adds a group to the list using a fluent GroupBuilder.
   *
   * @param configure - A callback that receives a GroupBuilder and returns it
   * @returns this (for chaining)
   */
  addGroup(configure: (group: GroupBuilder) => GroupBuilder): this {
    const group = configure(GroupBuilder.create(this.id)).build();
    this.groups.push(group);

    // merge group's valueItemMap into the list's valueItemMap
    group.valueItemMap.forEach((item, value) => {
      this.valueItemMap.set(value, item);
    });

    return this;
  }

  /**
   * Builds and returns a complete MultiSelectList.
   * Will throw an error if no groups were added.
   */
  build(): StatefulList {
    if (this.groups.length === 0) {
      throw new Error('List must contain at least one group.');
    }

    return {
      id: this.id,
      groups: this.groups as [GroupListItem, ...GroupListItem[]],
      valueItemMap: this.valueItemMap,
    };
  }

  static clone(list: StatefulList): StatefulList {
    return deepCopyList(list);
  }

  static create(id?: string): ListBuilder {
    return new ListBuilder(id);
  }
}

/**
 * Creates a new instance of a MultiSelectListBuilder.
 * Use this to build structured MultiSelectList data safely and fluently.
 */
export function createListItems(id?: string): ListBuilder {
  return new ListBuilder(id);
}

/**
 * Add group to an existing StatefulList by creating a new group.
 *
 * @param list - The existing StatefulList to add items to
 * @param configure - A callback that receives a GroupBuilder to configure the new group
 * @returns A new StatefulList instance with the added group
 * @example
 * const updatedList = addItemsToList(existingList, (group) =>
 *   group
 *     .addHeader('New Group')
 *     .addItem(10, 'New Item 1')
 *     .addItem(11, 'New Item 2')
 * );
 */
export function addGroupedItemsToList(
  list: StatefulList,
  configure: (group: GroupBuilder) => GroupBuilder,
): StatefulList {
  const builder = new ListBuilder(list.id);
  // Copy existing groups, carrying their value items into the list-level map
  list.groups.forEach((group) => {
    builder['groups'].push(group);
    group.valueItemMap.forEach((item, value) => {
      builder['valueItemMap'].set(value, item);
    });
  });
  // Add new group
  builder.addGroup(configure);
  return builder.build();
}

type ExistingGroupConfig = {
  list: StatefulList;
  groupIndex: number;
  configure: (group: GroupBuilder) => GroupBuilder;
  addToEnd?: boolean;
  preventThrow?: boolean;
};

/**
 * Add items to an existing group in a StatefulList by group index.
 *
 * @param list
 * @param groupIndex
 * @param configure
 * @returns A new StatefulList instance with the added items in the specified group
 * @example
 * ```
 * const updatedList = addItemsToExistingGroup(existingList, 0, (group) =>
 *  group
 *   .addItem(10, 'Additional Item 1')
 *   .addItem(11, 'Additional Item 2')
 * );
 * ```
 * @throws Error if the groupIndex is invalid
 *
 */
export function addItemsToExistingGroup({
  list,
  groupIndex,
  configure,
  addToEnd = true,
  preventThrow = false,
}: ExistingGroupConfig): StatefulList {
  if (groupIndex < 0 || groupIndex >= list.groups.length) {
    if (preventThrow) return list;
    throw new Error('Invalid group index.');
  }

  const builder = new ListBuilder();
  // Copy existing groups, modifying the target group
  list.groups.forEach((group, index) => {
    if (index === groupIndex) {
      // Create a GroupBuilder initialized with existing items
      const groupBuilder = new GroupBuilder(list.id);

      if (!addToEnd) {
        configure(groupBuilder); // Apply configuration before adding existing items
      }

      group.items.forEach((item) => {
        if (isHeaderItem(item)) {
          groupBuilder.addHeader(item.label);
        } else {
          groupBuilder.addItem(
            item.value,
            item.label,
            item.itemCallback,
            item.icon,
            item.variant,
          );
        }
      });

      if (addToEnd) {
        configure(groupBuilder); // Apply configuration after adding existing items
      }

      // Add the modified group
      if (addToEnd) {
        builder['groups'].push(groupBuilder.build());
      } else {
        builder['groups'].unshift(groupBuilder.build());
      }
    } else {
      // Just copy the group as is
      builder['groups'].push(group);
    }
  });

  return builder.build();
}

/**
 * Creates a deep copy of a StatefulList to ensure immutability.
 * This is useful when you need to modify a list without affecting the original instance.
 *
 * @param list - The StatefulList to deep copy
 * @returns A new StatefulList instance that is a deep copy of the input
 */
export function deepCopyList(list: StatefulList): StatefulList {
  const valueItemMap = new Map<ListValue, ValueListItem>();

  list.valueItemMap.forEach((item, value) => {
    valueItemMap.set(value, {
      value: item.value,
      label: item.label,
      icon: item.icon,
      focusId: item.focusId,
      itemCallback: item.itemCallback,
      variant: item.variant,
    });
  });

  return {
    id: list.id,
    valueItemMap,
    groups: list.groups.map((group) => ({
      valueItemMap: new Map(group.valueItemMap),
      items: group.items.map((item) =>
        isHeaderItem(item)
          ? { ...item }
          : {
              value: item.value,
              label: item.label,
              icon: item.icon,
              focusId: item.focusId,
              itemCallback: item.itemCallback,
              variant: item.variant,
            },
      ),
    })) as EnforcedGroupListItems,
  };
}
